import { useTranslation } from "react-i18next";
import type { PestSuggestion } from "@/lib/pests/pestSuggestion";
import { cn } from "@/lib/utils";

/**
 * LAS PLAGAS SUGERIDAS, UN TOQUE CADA UNA (F9, 26-09-2026).
 *
 * Debajo del campo «Contra qué plaga» de crear tarea y en la tarjeta de la
 * pantalla del trabajador. Cada botón dice de dónde sale la sugerencia («del
 * parte», «de la receta»): quien toca tiene que saber por qué se le propone.
 * Sin sugerencias no pinta nada —ni «sin sugerencias»—: es lo normal.
 */
export function PestSuggestionChips({
  suggestions,
  pestName,
  onPick,
  disabled = false,
  showLabel = true,
  className,
}: {
  suggestions: readonly PestSuggestion[];
  /** El nombre de una plaga por su id (el de la lista que ya tiene quien llama). */
  pestName: (pestId: string) => string | null | undefined;
  onPick: (pestId: string) => void;
  disabled?: boolean;
  /** «Sugerida:» delante; la tarjeta del trabajador ya lo pregunta en su título. */
  showLabel?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const visibles = suggestions.filter((s) => !!pestName(s.pestId));
  if (visibles.length === 0) return null;
  return (
    <div
      role="group"
      aria-label={t("pests.suggestion.label", "Sugerida")}
      className={cn("flex flex-wrap items-center gap-1.5", className)}
      data-testid="pest-suggestions"
    >
      {showLabel && (
        <span className="text-[11px] font-semibold text-muted-foreground">
          {t("pests.suggestion.label", "Sugerida")}:
        </span>
      )}
      {visibles.map((s) => (
        <button
          key={s.pestId}
          type="button"
          disabled={disabled}
          onClick={() => onPick(s.pestId)}
          data-testid="pest-suggestion"
          data-source={s.source}
          className="inline-flex min-h-[36px] items-center rounded-full border border-[#b6dded] bg-white px-3 py-1 text-xs font-semibold text-[#0c1f3d] shadow-sm transition-transform active:scale-95 disabled:opacity-50"
        >
          <span className="min-w-0 truncate">{pestName(s.pestId)}</span>
          <span className="ml-1 shrink-0 font-normal text-muted-foreground">
            · {s.source === "foco" ? t("pests.suggestion.fromFocus", "del parte") : t("pests.suggestion.fromRecipe", "de la receta")}
          </span>
        </button>
      ))}
    </div>
  );
}

export default PestSuggestionChips;
